import { Injectable } from '@angular/core';
import { DialogService, DialogRef, DialogCloseResult } from '@progress/kendo-angular-dialog';
import { Observable } from 'rxjs';

export interface KendoDialogOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  width?: number;
  height?: number;
  minWidth?: number;
}

export interface KendoDialogResult {
  confirmed: boolean;
  action: 'confirm' | 'cancel' | 'close';
}

@Injectable({
  providedIn: 'root'
})
export class KendoDialogService {
  constructor(private dialogService: DialogService) {}

  /**
   * Show a confirmation dialog
   * @param options Dialog configuration options
   * @returns Observable that emits true if confirmed, false otherwise
   */
  confirm(options: KendoDialogOptions): Observable<boolean> {
    return new Observable(observer => {
      const dialog: DialogRef = this.dialogService.open({
        title: options.title,
        content: options.message,
        actions: [
          { text: options.cancelText || 'Cancel', primary: false },
          { text: options.confirmText || 'Confirm', primary: true }
        ],
        width: options.width || 450,
        height: options.height,
        minWidth: options.minWidth || 250
      });

      dialog.result.subscribe((result: any) => {
        // Check if result is DialogCloseResult or has a text property
        const isConfirmed = result instanceof DialogCloseResult
          ? false
          : result.text === (options.confirmText || 'Confirm');

        observer.next(isConfirmed);
        observer.complete();
      });
    });
  }

  /**
   * Show a delete confirmation dialog (styled for danger)
   * @param itemName Name of the item being deleted
   * @param message Optional custom message
   * @returns Observable that emits true if confirmed, false otherwise
   */
  confirmDelete(itemName: string, message?: string): Observable<boolean> {
    return this.confirm({
      title: 'Confirm Delete',
      message: message || `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
  }

  /**
   * Show an alert dialog (single OK button)
   * @param title Dialog title
   * @param message Dialog message
   * @returns Observable that completes when dialog is closed
   */
  alert(title: string, message: string): Observable<void> {
    return new Observable(observer => {
      const dialog: DialogRef = this.dialogService.open({
        title: title,
        content: message,
        actions: [
          { text: 'OK', primary: true }
        ],
        width: 450,
        minWidth: 250
      });

      dialog.result.subscribe(() => {
        observer.next();
        observer.complete();
      });
    });
  }

  /**
   * Show a success alert
   * @param message Success message
   * @param title Optional custom title (defaults to "Success")
   */
  success(message: string, title: string = 'Success'): Observable<void> {
    return this.alert(title, message);
  }

  /**
   * Show an error alert
   * @param message Error message
   * @param title Optional custom title (defaults to "Error")
   */
  error(message: string, title: string = 'Error'): Observable<void> {
    return this.alert(title, message);
  }

  /**
   * Show a warning alert
   * @param message Warning message
   * @param title Optional custom title (defaults to "Warning")
   */
  warning(message: string, title: string = 'Warning'): Observable<void> {
    return this.alert(title, message);
  }

  /**
   * Show a custom content dialog with a component
   * @param component Component to display in the dialog
   * @param data Data to pass to the component
   * @param options Dialog configuration options
   * @returns DialogRef for manual control
   */
  openCustom(
    component: any,
    data?: any,
    options?: Partial<KendoDialogOptions>
  ): DialogRef {
    const dialogRef = this.dialogService.open({
      title: options?.title || 'Dialog',
      content: component,
      width: options?.width || 600,
      height: options?.height,
      minWidth: options?.minWidth || 250
    });

    // Pass data to the component instance if provided
    if (data && dialogRef.content && dialogRef.content.instance) {
      Object.assign(dialogRef.content.instance, data);
    }

    return dialogRef;
  }

  /**
   * Show a dialog with detailed result information
   * @param options Dialog configuration options
   * @returns Observable with detailed result information
   */
  confirmWithDetails(options: KendoDialogOptions): Observable<KendoDialogResult> {
    return new Observable(observer => {
      const dialog: DialogRef = this.dialogService.open({
        title: options.title,
        content: options.message,
        actions: [
          { text: options.cancelText || 'Cancel', primary: false },
          { text: options.confirmText || 'Confirm', primary: true }
        ],
        width: options.width || 450,
        height: options.height,
        minWidth: options.minWidth || 250
      });

      dialog.result.subscribe((result: any) => {
        let action: 'confirm' | 'cancel' | 'close';
        let confirmed: boolean;

        if (result instanceof DialogCloseResult) {
          action = 'close';
          confirmed = false;
        } else if (result.text === (options.confirmText || 'Confirm')) {
          action = 'confirm';
          confirmed = true;
        } else {
          action = 'cancel';
          confirmed = false;
        }

        observer.next({ confirmed, action });
        observer.complete();
      });
    });
  }

  /**
   * Show an input prompt dialog
   * @param promptText The prompt message
   * @param options Optional configuration
   * @returns Observable that emits the entered value if confirmed, null otherwise
   */
  prompt(promptText: string, options?: {
    title?: string;
    placeholder?: string;
    confirmText?: string;
    cancelText?: string;
    width?: number;
  }): Observable<string | null> {
    return new Observable(observer => {
      // Import the component dynamically to avoid circular dependencies
      import('../components/kendo-input-dialog/kendo-input-dialog.component').then(m => {
        const dialog: DialogRef = this.dialogService.open({
          content: m.KendoInputDialogComponent,
          width: options?.width || 450,
          minWidth: 250,
        });

        // Pass data to the component
        const instance = dialog.content.instance;
        instance.promptText = promptText;
        instance.confirmText = options?.confirmText || 'OK';
        instance.cancelText = options?.cancelText || 'Cancel';
        instance.placeholder = options?.placeholder || '';

        dialog.result.subscribe((result: any) => {
          // Handle confirmed result
          if (result && result.confirmed) {
            observer.next(result.value);
          } else {
            // Handle cancel, close button, or clicking outside
            observer.next(null);
          }
          observer.complete();
        });
      });
    });
  }
}
